<template>
  <modal-dialog
    visible
    max-width="900"
    card-height="760"
    card-max-height="80vh"
    container-class="pa-0 ma-0 h-100"
    card-class="pa-0 ma-0"
    persistent
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :title="steps.length <= 2 && step ? step.label : undefined"
  >
    <!-- stepper -->
    <v-stepper
      v-model="stepNo"
      class="elevation-0 stepper-h100"
      :alt-labels="$vuetify.breakpoint.smAndUp"
      @change="stepChanged"
    >
      <!-- stepper header -->
      <v-stepper-header
        v-if="stepKeys.length > 2"
        class="elevation-0"
        flat
      >
        <template v-for="(ss, i) in steps">
          <v-stepper-step
            :key="`${i}-step`"
            color="blue"
            :complete="completedSteps.includes(ss.key)"
            :step="i+1"
            edit-icon="$complete"
            :editable="editableSteps.includes(ss.key)"
          >
            {{ ss.label }}
          </v-stepper-step>
          <v-divider
            v-if="i < steps.length-1"
            :key="`${i}-divider`"
          />
        </template>
      </v-stepper-header>
      <v-divider />

      <!-- stepper items -->
      <v-stepper-items>
        <!-- payment success -->
        <v-stepper-content
          v-if="stepKeys.includes(PaymentWizardStepKey.PlanSelector)"
          :step="stepKeys.indexOf(PaymentWizardStepKey.PlanSelector)+1"
        >
          <v-card>
            <plan-selector />
          </v-card>
        </v-stepper-content>

        <!-- billing details -->
        <v-stepper-content
          v-if="stepKeys.includes(PaymentWizardStepKey.BillingDetails)"
          :step="stepKeys.indexOf(PaymentWizardStepKey.BillingDetails)+1"
        >
          <v-card>
            <p
              v-if="$vuetify.breakpoint.xsOnly"
              class="ma-0 pa-0 text-caption font-weight-bold text--primary"
            >
              {{ $t('label.billing_details') }}
            </p>
            <billing-details
              embedded
              :mode="mode"
            />
          </v-card>
        </v-stepper-content>

        <!-- payment details -->
        <v-stepper-content
          v-if="stepKeys.includes(PaymentWizardStepKey.PaymentDetails)"
          :step="stepKeys.indexOf(PaymentWizardStepKey.PaymentDetails)+1"
        >
          <v-card>
            <payment-details
              :mode="mode"
              :price="paymentDetailsPrice"
              :preview-invoice="paymentDetailsPreviewInvoice"
              :invoice="paymentDetailsInvoice"
              :prices="paymentDetailsPrices"
            />
          </v-card>
        </v-stepper-content>

        <!-- new payment method -->
        <v-stepper-content
          v-if="stepKeys.includes(PaymentWizardStepKey.NewPaymentMethod)"
          :step="stepKeys.indexOf(PaymentWizardStepKey.NewPaymentMethod)+1"
        >
          <v-card>
            <payment-methods
              :mode="mode"
              :client-secret="paymentMethodsClientSecret"
            />
          </v-card>
        </v-stepper-content>

        <!-- payment success -->
        <v-stepper-content
          v-if="stepKeys.includes(PaymentWizardStepKey.PaymentSuccess)"
          :step="stepKeys.indexOf(PaymentWizardStepKey.PaymentSuccess)+1"
        >
          <v-card>
            <payment-success
              :invoice="paymentSuccessInvoice"
              :setup-intent="paymentSuccessIntent"
            />
          </v-card>
        </v-stepper-content>

        <!-- end stepper items -->
      </v-stepper-items>
    </v-stepper>

    <!-- wizard actions -->
    <template #actions>
      <v-spacer />
      <v-btn
        v-if="!step || step.hideSubmit !== true"
        :disabled="step && disabledSteps.includes(step.key)"
        :color="step?.submitColor ?? 'primary'"
        @click="submit"
      >
        {{ submitText }}
      </v-btn>
      <v-btn
        v-if="!step || step.hideCancel !== true"
        text
        color="error"
        @click="cancel"
      >
        {{ $t('button.cancel') }}
      </v-btn>
    </template>
  </modal-dialog>
</template>

<script setup lang="ts">
import {
  PropType, ref, Ref, watch, onMounted, computed, provide, onBeforeMount, nextTick,
} from 'vue';
import { PaymentWizardMode, PaymentWizardStep, PaymentWizardStepKey } from '@/services/payment-wizard-utils';
import {
  Customer, Invoice, Price, SetupIntent,
} from '@/api/api-billing';
import i18n from '@/plugins/i18n';
import store from '@/store/store';
import BillingDetails from './PaymentBillingDetails.vue';
import PaymentDetails, { PaymentFinishedEvent } from './PaymentDetails.vue';
import PaymentSuccess from './PaymentSuccess.vue';
import PlanSelector, { PlanSelectedEvent } from './PlanSelector.vue';
import PaymentMethods, { NewPaymentMethodEvent } from './PaymentMethods.vue';

const props = defineProps({
  mode: { type: String as PropType<PaymentWizardMode>, default: undefined },

  price: { type: Object as PropType<Price|undefined>, default: undefined },
  previewInvoice: { type: Object as PropType<Invoice|undefined>, default: undefined },

  invoice: { type: Object as PropType<Invoice|undefined>, default: undefined },

  finishedInvoice: { type: Object as PropType<Invoice|undefined>, default: undefined },
  finishedIntent: { type: Object as PropType<SetupIntent|undefined>, default: undefined },

  setupIntent: { type: Object as PropType<SetupIntent|undefined>, default: undefined },

  prices: { type: Array as PropType<Array<Price>|undefined>, default: undefined },
});

const paymentDetailsPrice = ref(undefined) as Ref<Price|undefined>;
const paymentDetailsPreviewInvoice = ref(undefined) as Ref<Invoice|undefined>;
const paymentDetailsInvoice = ref(undefined) as Ref<Invoice|undefined>;
const paymentDetailsPrices = ref(undefined) as Ref<Price[]|undefined>;
const paymentSuccessInvoice = ref(undefined) as Ref<Invoice|undefined>;
const paymentSuccessIntent = ref(undefined) as Ref<SetupIntent|undefined>;
const paymentMethodsClientSecret = ref(undefined) as Ref<string|undefined>;

const emit = defineEmits(['close']);

const stepKeys = ref([]) as Ref<PaymentWizardStepKey[]>;

const steps = ref([]) as Ref<PaymentWizardStep[]>;
const disabledSteps = ref([]) as Ref<string[]>;
const completedSteps = ref([]) as Ref<string[]>;
const editableSteps = ref([]) as Ref<string[]>;

const stepNo = ref(1) as Ref<number>;
const step = computed(() => steps.value[stepNo.value - 1]);

const stepMap = new Map<string, PaymentWizardStep>();
const addStep = (s: PaymentWizardStep) => {
  stepMap.set(s.key, s);
  steps.value = stepKeys.value.flatMap((sk) => stepMap.get(sk) ?? []);
};
provide('addStep', addStep);

const stepChanged = (s: PaymentWizardStep) => {
  // update step in steps
  stepMap.set(s.key, s);
  steps.value = stepKeys.value.flatMap((sk) => stepMap.get(sk) ?? []);

  // enable/disable step
  if (s.complete && disabledSteps.value.includes(s.key)) {
    disabledSteps.value = disabledSteps.value.filter((dsk) => dsk !== s.key);
  }
  if (!s.complete && !disabledSteps.value.includes(s.key)) {
    disabledSteps.value = [...disabledSteps.value, s.key];
  }
};
provide('stepChanged', stepChanged);

const submit = async () => {
  // submit step and
  const submitAction = step.value.submitAction ?? null;
  if (submitAction) {
    const res = await submitAction();
    // eslint-disable-next-line no-use-before-define
    const ok = handleResult(res, step.value.key);
    if (!ok) return false;
  }

  // set step as completed
  if (!completedSteps.value.includes(step.value!.key)) completedSteps.value.push(step.value!.key);

  // last step -> close wizard
  if (stepNo.value === stepKeys.value.length) { emit('close'); return true; }

  // handleResult to prepareAction reactivity fix
  await nextTick();

  // prepare the next step
  const prepareAction = (stepNo.value < stepKeys.value.length) && steps.value[stepNo.value].prepareAction;
  if (prepareAction) {
    const ok = await prepareAction();
    if (!ok) return false;
  }

  // move to the next step
  stepNo.value += 1;
  return true;
};
const submitStep = (s: PaymentWizardStep) => {
  if (s.key !== step.value.key) { return; }
  submit();
};
provide('submitStep', submitStep);

const cancel = async () => {
  const cancelAction = step.value.cancelAction ?? null;
  if (cancelAction) await cancelAction();

  emit('close');
};

const submitText = computed(
  () => step.value?.submitText
  ?? (stepNo.value < stepKeys.value.length ? i18n.tc('button.continue') : i18n.tc('button.ok')),
);

// watch(
//   () => steps.value,
//   (value) => { console.log('steps array changed: ', value); },
// );

const handleResult = (result: any, key: string) => {
  console.log('handleResult: ', result);

  if (!result) return false;

  if (key === PaymentWizardStepKey.PlanSelector) {
    const event = result as PlanSelectedEvent;
    paymentDetailsPrice.value = event.price;
    paymentDetailsPreviewInvoice.value = event.previewInvoice;
  } else if (key === PaymentWizardStepKey.PaymentDetails) {
    const event = result as PaymentFinishedEvent;
    paymentSuccessInvoice.value = event.finishedInvoice;
    paymentSuccessIntent.value = event.finishedSetupIntent;
  } else if (key === PaymentWizardStepKey.NewPaymentMethod) {
    const event = result as NewPaymentMethodEvent;
    paymentSuccessIntent.value = event.finishedSetupIntent;
  }

  return true;
};

// create steps
const createSteps = () => {
  const keys = [] as PaymentWizardStepKey[];

  // subscription only
  if ([PaymentWizardMode.Subscription].includes(props.mode) && !props.price) {
    keys.push(PaymentWizardStepKey.PlanSelector);
  }

  // billing details or no customer -> create customer details
  const customer = store.getters.billingCustomer as Customer;
  if ([PaymentWizardMode.BillingDetails].includes(props.mode)
  || ([PaymentWizardMode.Subscription, PaymentWizardMode.Invoice, PaymentWizardMode.Prices].includes(props.mode)
    && !customer)
  ) {
    keys.push(PaymentWizardStepKey.BillingDetails);
  }

  // payment details and
  if ([PaymentWizardMode.Subscription, PaymentWizardMode.Invoice, PaymentWizardMode.Prices].includes(props.mode)) {
    keys.push(PaymentWizardStepKey.PaymentDetails);
  }

  // new payment method
  if (props.mode === PaymentWizardMode.NewPaymentMethod) {
    keys.push(PaymentWizardStepKey.NewPaymentMethod);
  }

  // success
  if ([PaymentWizardMode.Subscription, PaymentWizardMode.Invoice,
    PaymentWizardMode.NewPaymentMethod, PaymentWizardMode.Result, PaymentWizardMode.Prices].includes(props.mode)) {
    keys.push(PaymentWizardStepKey.PaymentSuccess);
  }

  stepKeys.value = keys;
  console.log('createSteps: ', keys);
};

onBeforeMount(() => {
  createSteps();

  paymentDetailsPrice.value = props.price;
  paymentDetailsPreviewInvoice.value = props.previewInvoice;
  paymentDetailsInvoice.value = props.invoice;
  paymentDetailsPrices.value = props.prices;
  paymentSuccessInvoice.value = props.finishedInvoice;
  paymentSuccessIntent.value = props.finishedIntent;
  paymentMethodsClientSecret.value = props.setupIntent?.clientSecret;
});

onMounted(async () => {
  const { prepareAction } = step.value;
  if (prepareAction) {
    const ok = await prepareAction();
    if (!ok) cancel();
  }
});

</script>

<style lang="scss">

  .stepper-h100 {
    display: flex;
    flex-direction: column;
    min-height: 100%;

    .v-stepper__items {
      flex: 1;
      min-height: inherit;
      display: flex;
      flex-direction: column;

      .v-stepper__content {
        min-height: inherit;
        flex:1;
        display: flex;
        flex-direction: column;

        .v-stepper__wrapper {
          min-height: inherit;
          flex:1;
          display: flex;
          flex-direction: column;

          .v-card {
            min-height: inherit;
            flex:1;
            display: flex;
            flex-direction: column;
          }
        }
      }
    }
  }

</style>
